import request from '../utils/request';
import type { ListResponse } from '../types/common';

export interface LogisticsProvider {
  id: number;
  name: string;
  code: string;
  website: string;
  logo: string;
  service_count: number;
  remarks: string;
  created_at: string;
  updated_at: string;
  created_by_name: string;
}

export interface LogisticsProviderQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
}

export interface ShippingPackaging {
  id: number;
  system_code: string;
  ownership_type: 'warehouse' | 'customer';
  warehouse: number | null;
  warehouse_name?: string | null;
  customer: number | null;
  customer_name?: string | null;
  price: string | number | null;
  packaging_type: 'carton' | 'shipping_bag' | 'pallet' | 'bubble_mailer' | 'other';
  condition: 'new' | 'used';
  is_active?: boolean;
  name?: string;
  remarks?: string;
  length_cm?: string | number | null;
  width_cm?: string | number | null;
  height_cm?: string | number | null;
  weight_g?: string | number | null;
  created_at?: string;
  updated_at?: string;
}

export interface ShippingPackagingQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  ownership_type?: string;
  packaging_type?: string;
  condition?: string;
  warehouse?: number;
  customer?: number;
  is_active?: boolean;
  charge_type?: 'charged' | 'free';
}

// 获取物流服务商列表
export const getLogisticsProviders = async (params: LogisticsProviderQueryParams) => {
  const response = await request.get<ListResponse<LogisticsProvider>>('/api/v1/logistics/providers/', { params });
  return response;
};

// 创建物流服务商；传 FormData 时会带上 logo 文件。这里的 axios 实例（request.ts）建实例时
// 固定写死了 Content-Type: application/json 当默认头，FormData 不会自动把它换成正确的
// multipart/form-data + boundary（axios 只在“没设置过”Content-Type 时才会自动处理，这里
// 已经显式设置过了，所以传 FormData 必须手动覆盖这个头，和 createCustomerAddress 是同一个坑）
export const createLogisticsProvider = async (data: Partial<LogisticsProvider> | FormData) => {
  const response = await request.post<LogisticsProvider>('/api/v1/logistics/providers/', data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
  });
  return response;
};

// 更新物流服务商（同上，传 FormData 可以带 logo 文件或 remove_logo 删除标记）
export const updateLogisticsProvider = async (id: number, data: Partial<LogisticsProvider> | FormData) => {
  const response = await request.patch<LogisticsProvider>(`/api/v1/logistics/providers/${id}/`, data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
  });
  return response;
};

// 删除物流服务商
export const deleteLogisticsProvider = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/providers/${id}/`);
  return response;
};

// ======================== 物流网关（Gateway）管理 ========================

export interface Gateway {
  id: number;
  name: string;
  code: string;
  gateway_type: string;
  gateway_type_display: string;
  is_active: boolean;
  health_status: string;
  health_status_display: string;
  health_checked_at: string | null;
  markup_type: 'none' | 'percentage' | 'fixed';
  markup_value: string | null;
  account_count: number;
  /** 网关 API 对接代码是否真的能把签名服务/保险要求传给承运商并体现在报价里
   * （不是承运商产品本身支不支持，是我们这套集成代码技术上能不能做到）。
   * 询价时客户要求了签名/保险，会跳过标记为不支持的网关账号。 */
  supports_signature: boolean;
  supports_insurance: boolean;
  remarks: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export interface GatewayQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_active?: boolean;
  gateway_type?: string;
  ordering?: string;
}

// 获取物流网关列表
export const getGateways = async (params: GatewayQueryParams) => {
  const response = await request.get<ListResponse<Gateway>>('/api/v1/logistics/gateways/', { params });
  return response;
};

// 更新物流网关（只能改启用状态、加价策略、签名/保险支持标记、备注——Adapter 路径仍走 Django 后台，账号/凭证见下方 GatewayAccount）
export const updateGateway = async (
  id: number,
  data: Partial<Pick<Gateway, 'is_active' | 'markup_type' | 'markup_value' | 'supports_signature' | 'supports_insurance' | 'remarks'>>
) => {
  const response = await request.patch<Gateway>(`/api/v1/logistics/gateways/${id}/`, data);
  return response;
};

export interface GatewayHealthCheckResult {
  id: number;
  code: string;
  // true 表示这个网关没有可用账号，没法测，跳过了（不代表网关本身有问题）
  skipped: boolean;
  reason?: string;
  health_status?: string;
  message?: string;
}

// 对所有启用的网关各挑一个启用账号，真实调用一次健康检查，结果写回后台并在这里返回
export const checkGatewaysHealth = async () => {
  const response = await request.post<{ results: GatewayHealthCheckResult[] }>('/api/v1/logistics/gateways/health-check/');
  return response;
};

// ======================== 网关账号（凭证）管理 ========================

export interface GatewayAccount {
  id: number;
  gateway: number;
  gateway_name: string;
  name: string;
  environment: 'sandbox' | 'production';
  environment_display: string;
  external_account_ref: string;
  balance: string | null;
  is_active: boolean;
  // 凭证只写不回显，列表/详情接口不会返回明文，这里只能看到"是否已配置"
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export interface GatewayAccountQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  gateway?: number;
  is_active?: boolean;
  environment?: string;
}

// 获取网关账号列表
export const getGatewayAccounts = async (params: GatewayAccountQueryParams) => {
  const response = await request.get<ListResponse<GatewayAccount>>('/api/v1/logistics/gateway-accounts/', { params });
  return response;
};

export interface GatewayAccountWritePayload {
  gateway?: number;
  name?: string;
  environment?: 'sandbox' | 'production';
  external_account_ref?: string;
  balance?: string | number | null;
  is_active?: boolean;
  // 新增时必填；编辑时留空/不传表示不修改凭证，只改其它字段
  credentials?: Record<string, unknown>;
}

// 新增网关账号（必须提供 credentials）
export const createGatewayAccount = async (data: GatewayAccountWritePayload) => {
  const response = await request.post<GatewayAccount>('/api/v1/logistics/gateway-accounts/', data);
  return response;
};

// 更新网关账号（不传 credentials 则保持凭证不变）
export const updateGatewayAccount = async (id: number, data: GatewayAccountWritePayload) => {
  const response = await request.patch<GatewayAccount>(`/api/v1/logistics/gateway-accounts/${id}/`, data);
  return response;
};

// 删除网关账号
export const deleteGatewayAccount = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/gateway-accounts/${id}/`);
  return response;
};

// ======================== 标签处理规则（按网关+承运商配置运单标签展示前要不要处理） ========================

export interface LabelProcessingRuleRegionBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  label?: string;
}

export interface LabelProcessingRuleRegionConfig {
  cover_boxes?: LabelProcessingRuleRegionBox[];
  protected_zones?: LabelProcessingRuleRegionBox[];
  [key: string]: unknown;
}

export interface LabelProcessingRule {
  id: number;
  gateway: number;
  gateway_name: string;
  gateway_code: string;
  // 留空(null)表示"这个网关下所有承运商都适用"的兜底规则
  logistics_provider: number | null;
  logistics_provider_name: string | null;
  processing_type: 'none' | 'cover_region';
  processing_type_display: string;
  region_config: LabelProcessingRuleRegionConfig;
  is_active: boolean;
  verified_at: string | null;
  remark: string;
  created_at: string;
  updated_at: string;
}

export interface LabelProcessingRuleQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  gateway?: number;
  logistics_provider?: number;
  processing_type?: string;
  is_active?: boolean;
}

export const getLabelProcessingRules = async (params: LabelProcessingRuleQueryParams) => {
  const response = await request.get<ListResponse<LabelProcessingRule>>('/api/v1/logistics/label-processing-rules/', { params });
  return response;
};

export type LabelProcessingRuleWritePayload = Partial<
  Pick<LabelProcessingRule, 'gateway' | 'logistics_provider' | 'processing_type' | 'region_config' | 'is_active' | 'verified_at' | 'remark'>
>;

export const createLabelProcessingRule = async (data: LabelProcessingRuleWritePayload) => {
  const response = await request.post<LabelProcessingRule>('/api/v1/logistics/label-processing-rules/', data);
  return response;
};

export const updateLabelProcessingRule = async (id: number, data: LabelProcessingRuleWritePayload) => {
  const response = await request.patch<LabelProcessingRule>(`/api/v1/logistics/label-processing-rules/${id}/`, data);
  return response;
};

export const deleteLabelProcessingRule = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/label-processing-rules/${id}/`);
  return response;
};

// ======================== 客户运单风险档案（保证金乘数 + 是否允许国际件，按客户手动设置） ========================

export interface CustomerRiskProfile {
  customer: number;
  customer_username: string;
  customer_code: string | null;
  company_name: string | null;
  deposit_multiplier: string;
  allow_international: boolean;
  remark: string;
  updated_by_name: string;
  updated_at: string;
  created_at: string;
}

// 没有为这个客户建过档案时后端返回 404——不是错误，代表"还没定制，按系统默认值处理"，
// 前端按这个默认值（乘数 1.0、不允许国际件）展示，不当成报错弹提示。
export const DEFAULT_CUSTOMER_RISK = { deposit_multiplier: '1.00', allow_international: false, remark: '' };

export const getCustomerRiskProfile = async (customerId: number | string) => {
  const response = await request.get<CustomerRiskProfile>(`/api/v1/logistics/customer-risk-profiles/${customerId}/`);
  return response;
};

// 保存永远用同一个请求（不用先查一次有没有档案再决定新建还是更新）：后端按 customer 做
// upsert，不管这个客户之前有没有档案，POST 一次就是最终想要的值。
export const saveCustomerRiskProfile = async (data: {
  customer: number | string;
  deposit_multiplier: string | number;
  allow_international: boolean;
  remark?: string;
}) => {
  const response = await request.post<CustomerRiskProfile>('/api/v1/logistics/customer-risk-profiles/', data);
  return response;
};

// ======================== 标准物流产品目录（承运商与物流产品管理 + 标准化映射页面下拉复用） ========================

export interface Service {
  id: number;
  logistics_provider: number;
  logistics_provider_name?: string | null;
  name: string;
  code: string;
  // 电池运输资质：拆成"化学类型 × 安装方式"六档，对照 USPS/UPS/FedEx 官方电池运输指南人工维护，
  // 不是自动生成的。除电池外的其它危险品（香水/压缩气体/化学品等）不支持任何物流产品运输，不需要字段。
  battery_lithium_ion_installed: boolean;
  battery_lithium_ion_packed: boolean;
  battery_lithium_ion_standalone: boolean;
  battery_lithium_metal_installed: boolean;
  battery_lithium_metal_packed: boolean;
  battery_lithium_metal_standalone: boolean;
  max_weight_kg: string | null;
  max_length_cm: string | null;
  max_width_cm: string | null;
  max_height_cm: string | null;
  estimated_transit_days_min: number | null;
  estimated_transit_days_max: number | null;
  // 风险保证金规则：购买运单时按 报价 × deposit_rate_percent 冻结保证金，再用最低/最高值裁剪。
  // 比例为 0（默认）表示不冻结保证金，不影响现有行为。
  deposit_rate_percent: string;
  deposit_min_amount: string;
  deposit_max_amount: string | null;
  // 该产品需要额外提供的资料说明（如清关所需的身份证/发票/税号，或行李渠道所需的机票/护照），
  // 每个产品的要求不一样，是手动填写的自由文本，不是固定选项
  required_documents: string;
  remarks: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string | null;
}

export interface ServiceQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  logistics_provider?: number;
  is_active?: boolean;
}

// 获取标准物流产品列表
export const getServices = async (params: ServiceQueryParams) => {
  const response = await request.get<ListResponse<Service>>('/api/v1/logistics/services/', { params });
  return response;
};

// 创建标准物流产品
export const createService = async (data: Partial<Service>) => {
  const response = await request.post<Service>('/api/v1/logistics/services/', data);
  return response;
};

// 更新标准物流产品
export const updateService = async (id: number, data: Partial<Service>) => {
  const response = await request.patch<Service>(`/api/v1/logistics/services/${id}/`, data);
  return response;
};

// 删除标准物流产品
export const deleteService = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/services/${id}/`);
  return response;
};

// ======================== Gateway 承运商/服务标准化映射 ========================

export interface ServiceMapping {
  id: number;
  gateway_account: number;
  gateway_name: string;
  gateway_account_name: string;
  external_carrier_code: string;
  external_service_code: string;
  logistics_provider: number | null;
  logistics_provider_name: string | null;
  service: number | null;
  service_name: string | null;
  is_confirmed: boolean;
  // 精细到"这一个网关账号的这一个具体报价"的开关：关闭后询价时会跳过这一条，不影响同一个
  // 标准产品在其它网关下的映射（对比 Service.is_active 是全局禁用某个标准产品）
  is_active: boolean;
  // 前端展示用：是否已经真正生效（is_confirmed 且承运商/产品都已关联），不是简单等于 is_confirmed
  is_mapped: boolean;
  // 加价策略覆盖：留空（null）表示沿用所在网关的默认加价方式，只对这一条网关报价生效
  markup_type: 'none' | 'percentage' | 'fixed' | null;
  markup_value: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceMappingQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_confirmed?: boolean;
  ordering?: string;
  // 按网关筛选（不是网关账号）：同一个网关下可能有多个账号，这个参数会筛出该网关名下所有账号的记录
  gateway?: number;
}

// 获取标准化映射列表
export const getServiceMappings = async (params: ServiceMappingQueryParams) => {
  const response = await request.get<ListResponse<ServiceMapping>>('/api/v1/logistics/service-mappings/', { params });
  return response;
};

// 更新标准化映射（关联承运商/产品、确认是否生效）——这几条是后端惰性发现自动创建的，不支持新建/删除
export const updateServiceMapping = async (
  id: number,
  data: Partial<Pick<ServiceMapping, 'logistics_provider' | 'service' | 'is_confirmed' | 'is_active' | 'markup_type' | 'markup_value'>>
) => {
  const response = await request.patch<ServiceMapping>(`/api/v1/logistics/service-mappings/${id}/`, data);
  return response;
};

// 获取发货包材列表
export const getShippingPackagings = async (params: ShippingPackagingQueryParams) => {
  const response = await request.get<ListResponse<ShippingPackaging>>('/api/v1/logistics/shipping-packagings/', { params });
  return response;
};

// 创建发货包材
export const createShippingPackaging = async (data: Partial<ShippingPackaging>) => {
  const response = await request.post<ShippingPackaging>('/api/v1/logistics/shipping-packagings/', data);
  return response;
};

// 更新发货包材
export const updateShippingPackaging = async (id: number, data: Partial<ShippingPackaging>) => {
  const response = await request.patch<ShippingPackaging>(`/api/v1/logistics/shipping-packagings/${id}/`, data);
  return response;
};

// 删除发货包材
export const deleteShippingPackaging = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/shipping-packagings/${id}/`);
  return response;
};

// 报价阶段：只要求 country + postal_code，其余字段选填
export interface AddressPayload {
  country: string;
  postal_code: string;
  state?: string;
  city?: string;
  address_line?: string;
  address_line2?: string;
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
  // 发件人/收件人自己的参考号，后端目前会原样忽略未识别字段，纯前端展示/预填用途
  reference_number?: string;
}

// 购买运单阶段：标准完整地址，name/address_line/city/state/phone 都是必填（前端表单层面校验）
export type FullAddressPayload = Required<Pick<AddressPayload, 'country' | 'postal_code' | 'name' | 'address_line' | 'city' | 'state' | 'phone'>> &
  Pick<AddressPayload, 'address_line2' | 'company' | 'email'>;

// 单个包裹的物理信息
export interface PackagePayload {
  weight_kg: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
}

export interface ShippingRateQuoteRequest {
  origin: AddressPayload;
  destination: AddressPayload;
  packages: PackagePayload[];
  signature_required?: boolean;
  insurance_amount?: number;
  // 电池以外的其它危险品（香水/压缩气体/化学品等）——公司政策不支持任何物流产品运输，
  // 传 true 后端会直接拒绝整个询价请求，不会返回任何报价
  contains_other_dangerous_goods?: boolean;
  battery_declared?: boolean;
  battery_chemistry?: 'lithium_ion' | 'lithium_metal';
  battery_installation?: 'installed' | 'packed' | 'standalone';
  // 仅管理员账号生效：跳过"跨 Gateway 去重、同一承运商/服务只保留最低价"，返回所有 Gateway
  // 的所有报价；非管理员账号即使传了这个字段，后端也会强制忽略，不会真的生效
  include_all_gateways?: boolean;
  // 仅员工/管理员代客户询价时可选传——传了之后保证金预估、国际件筛选会按这个客户真实的
  // 风险档案计算，不传则按系统默认值给一个通用预估（不代表最终购买时的真实金额/权限）；
  // 客户账号自己询价不需要传，后端会按登录身份自动解析，传了也会被忽略。
  customer_id?: number;
}

export interface ShippingRateQuoteResult {
  // 多包裹时，一条汇总报价对应每个包裹各一条 RateQuote 记录
  rate_quote_ids: number[];
  carrier_name: string;
  // 承运商代码；未标准化时跟 carrier_name 一样是原始承运商标识
  carrier_code?: string;
  // 承运商 Logo，未标准化或该承运商没传 Logo 时为空
  carrier_logo?: string | null;
  service_name: string;
  price: string;
  // 按物流产品的风险保证金规则逐包裹算好再相加的保证金，和购买时实际冻结的口径一致；
  // 未标准化的产品没有 Service 可以算，为 null（不是 0——不能假装知道保证金是多少）
  deposit_amount: string | null;
  currency: string;
  transit_days_min: number | null;
  transit_days_max: number | null;
  is_mapped: boolean;
  // 收件地址是否为住宅地址（系统判定），只有美国地址会有值，其他国家为 null
  is_residential: boolean | null;
  // 报价走的是哪个 Gateway；后端只对管理员账号返回这个字段，其他角色该字段不存在
  gateway_name?: string;
  // Gateway 加价前的原始成本价；同样只对管理员账号返回，其他角色该字段不存在
  cost_price?: string;
}

// 物流费用查询：后端要并发问询多个 Gateway 账号，取决于最慢的那一个账号（真实遇到过
// Pitney Bowes 沙盒环境自己就要接近 30 秒才返回），这里单独放宽超时时间，不影响全局默认值。
export const getShippingRateQuote = async (payload: ShippingRateQuoteRequest) => {
  const response = await request.post<ShippingRateQuoteResult[]>(
    '/api/v1/logistics/rate-quotes/', payload, { timeout: 60000 },
  );
  return response;
};

export interface CreateShipmentRequest {
  rate_quote_ids: number[];
  origin: FullAddressPayload;
  destination: FullAddressPayload;
  remarks?: string;
  signature_required?: boolean;
  insurance_amount?: number;
  battery_declared?: boolean;
  battery_chemistry?: 'lithium_ion' | 'lithium_metal';
  battery_installation?: 'installed' | 'packed' | 'standalone';
  // 管理员/员工代客户购买运单时必须指定，后端会按登录身份自动解析、这里传了也会被忽略——
  // 客户账号自己购买不需要传这个字段
  customer_id?: number;
}

// 购买运单弹窗里"所属客户"筛选框用：按关键字实时查询客户（编码/公司名/用户名/邮箱模糊匹配），
// 仅管理员/员工账号能拿到数据，客户账号调这个接口会被后端过滤成空列表
export interface CustomerOption {
  id: number;
  customer_code: string;
  company_name: string;
  username: string;
}

export const searchCustomers = async (keyword: string): Promise<CustomerOption[]> => {
  const response = await request.get('/api/v1/users/', {
    // 只要主账号——子账号购买运单时账单应该记在主账号名下，不单独在这个筛选框里出现
    params: { role: 'customer', sub_role: 'main_account', q: keyword || undefined, page: 1, page_size: 20 },
  });
  const data = response.data;
  const items: any[] = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return items.map((u) => ({
    id: u.id,
    customer_code: u.customer_code || '',
    company_name: u.company_name || '',
    username: u.username || '',
  }));
};

// 物流记录：已经通过物流引擎真实购买的运单（只读，购买流程自动生成，不在这个页面编辑）。
// gateway_name/gateway_cost_price 只有管理员/员工账号能拿到真实值，客户拿到的是 null，
// 不是字段缺失——前端不用分角色处理"这个字段存不存在"。
export interface ShipmentAddressSummary {
  name: string;
  company: string;
  phone: string;
  email: string;
  address_line: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface Shipment {
  id: number;
  tracking_number: string;
  external_shipment_id: string;
  carrier_name: string;
  carrier_code: string;
  carrier_logo: string | null;
  service_name: string | null;
  status: 'pending' | 'in_transit' | 'delivered' | 'exception' | 'returned';
  status_display: string;
  record_status: 'active' | 'voided';
  record_status_display: string;
  // 一次购买多个包裹时，同一批次的记录共享同一个 purchase_batch_id；单包裹购买是 null。
  // batch_size 是这个批次一共有几个包裹（单包裹购买恒为 1），列表页用它显示"N件套"角标。
  purchase_batch_id: string | null;
  batch_size: number;
  // 购买时的收发件地址快照；老数据（该字段上线之前购买的运单）没有快照，是 null
  sender: ShipmentAddressSummary | null;
  recipient: ShipmentAddressSummary | null;
  weight_kg: string | null;
  length_cm: string | null;
  width_cm: string | null;
  height_cm: string | null;
  currency: string;
  quoted_shipping_fee: string;
  deposit_frozen: string;
  // 保证金是否已经解冻（60 天到期自动解冻，或人工处理）——deposit_frozen 本身解冻后
  // 不会清零，不能用它判断当前状态，只能看这个字段。
  deposit_released: boolean;
  final_shipping_fee: string;
  // 购买时声明的签名/保险/电池信息快照；该字段上线之前购买的老数据都是默认值
  // （false / null / 空字符串），不代表当时真的没有声明，只是没留下记录
  signature_required: boolean;
  insurance_amount: string | null;
  battery_declared: boolean;
  battery_chemistry: string;
  battery_installation: string;
  gateway_name: string | null;
  gateway_cost_price: string | null;
  customer_name: string;
  customer_code: string | null;
  created_by_name: string | null;
  label_url: string | null;
  remarks: string;
  created_at: string;
}

export interface ShipmentQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  record_status?: string;
  // 保证金是否已解冻——'true'=已解冻，'false'=未解冻，不传=不筛选
  deposit_released?: 'true' | 'false';
  provider?: number;
  service?: number;
  // 按网关（Shippo/BBL/...）筛选，不是按具体网关账号
  gateway_account__gateway?: number;
  customer?: number;
  created_at__gte?: string;
  created_at__lte?: string;
  ordering?: string;
}

export const getShipments = async (params: ShipmentQueryParams) => {
  const response = await request.get<ListResponse<Shipment>>('/api/v1/logistics/shipment-records/', { params });
  return response;
};

// 手动作废一条运单记录（单据状态，不涉及退款）——仅员工/管理员可调用，后端会校验权限
export const cancelShipment = async (id: number, reason?: string) => {
  const response = await request.post<Shipment>(`/api/v1/logistics/shipment-records/${id}/cancel/`, { reason });
  return response;
};

// 单个包裹的购买结果——多包裹购买时，每个包裹各自成功或失败，不是全有或全无
export interface PackagePurchaseResult {
  rate_quote_id: number;
  success: boolean;
  shipment_id: number | null;
  tracking_number: string | null;
  label_url: string | null;
  error: string | null;
}

export interface CreateShipmentResult {
  results: PackagePurchaseResult[];
}

// 购买运单
export const createShipment = async (payload: CreateShipmentRequest) => {
  const response = await request.post<CreateShipmentResult>('/api/v1/logistics/shipments/', payload);
  return response;
};

// 地址解析结果——只有识别到的字段才会出现，具体字段取决于粘贴文本的内容
export interface ParsedAddressResult {
  recipient_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  email?: string;
  zip_lookup_city?: string;
  zip_lookup_state?: string;
}

/**
 * 地址解析：复用 instruction 模块（创建一件代发指令）里已有的解析接口，本质是一套
 * 针对美国地址书写习惯调的正则启发式（州两位缩写+5位邮编、街道类英文关键词等），
 * 对非美国地址无法正确识别，调用方只应该在国家为 US 时才展示这个入口。
 */
export const parseAddressText = async (text: string) => {
  const response = await request.post<{ parsed: ParsedAddressResult }>(
    '/api/v1/instruction/instructions/address-parse/',
    { text }
  );
  return response;
};

// ======================== 客户地址库 ========================

export interface CustomerAddress {
  id: number;
  customer: number;
  customer_name?: string | null;
  label?: string;
  address_type: 'sender' | 'recipient' | 'both';
  country: string;
  postal_code: string;
  state: string;
  city: string;
  address_line: string;
  address_line2?: string;
  name: string;
  company?: string;
  phone: string;
  email?: string;
  remarks?: string;
  id_type?: 'id_card' | 'passport' | 'ssn' | 'ein' | 'other' | '';
  // 无权限查看证件信息的账号，这里会是 null（后端按权限表屏蔽，不是前端隐藏）
  id_number?: string | null;
  id_photo_front_url?: string | null;
  id_photo_back_url?: string | null;
  is_default_origin: boolean;
  is_default_recipient: boolean;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name?: string | null;
}

export interface CustomerAddressQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  address_type?: string;
  is_active?: boolean;
  is_default_origin?: boolean;
  is_default_recipient?: boolean;
  customer?: number;
  country?: string;
}

// 获取地址库列表
export const getCustomerAddresses = async (params: CustomerAddressQueryParams) => {
  const response = await request.get<ListResponse<CustomerAddress>>('/api/v1/logistics/customer-addresses/', { params });
  return response;
};

// 地址库表单值（不含照片文件，照片通过 idPhotoFront/idPhotoBack 单独传入）
export interface CustomerAddressFormValues {
  customer?: number;
  label?: string;
  address_type: 'sender' | 'recipient' | 'both';
  country: string;
  postal_code: string;
  state: string;
  city: string;
  address_line: string;
  address_line2?: string;
  name: string;
  company?: string;
  phone: string;
  email?: string;
  remarks?: string;
  id_type?: string;
  id_number?: string;
  is_active?: boolean;
  // 编辑时不选新文件、单纯删除已保存的证件照片用这两个开关
  remove_id_photo_front?: boolean;
  remove_id_photo_back?: boolean;
}

function buildCustomerAddressFormData(
  values: CustomerAddressFormValues,
  files?: { idPhotoFront?: File | null; idPhotoBack?: File | null }
): FormData {
  const fd = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    fd.append(key, String(value));
  });
  if (files?.idPhotoFront) fd.append('id_photo_front', files.idPhotoFront);
  if (files?.idPhotoBack) fd.append('id_photo_back', files.idPhotoBack);
  return fd;
}

// 创建地址库记录
export const createCustomerAddress = async (
  values: CustomerAddressFormValues,
  files?: { idPhotoFront?: File | null; idPhotoBack?: File | null }
) => {
  const fd = buildCustomerAddressFormData(values, files);
  const response = await request.post<CustomerAddress>('/api/v1/logistics/customer-addresses/', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

// 更新地址库记录
export const updateCustomerAddress = async (
  id: number,
  values: Partial<CustomerAddressFormValues>,
  files?: { idPhotoFront?: File | null; idPhotoBack?: File | null }
) => {
  const fd = buildCustomerAddressFormData(values as CustomerAddressFormValues, files);
  const response = await request.patch<CustomerAddress>(`/api/v1/logistics/customer-addresses/${id}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

// 删除地址库记录
export const deleteCustomerAddress = async (id: number) => {
  const response = await request.delete(`/api/v1/logistics/customer-addresses/${id}/`);
  return response;
};

// 设为默认发货/收件地址：同一客户名下同类型只能有一个默认地址，后端会自动把其它记录的标记清掉
export const setCustomerAddressDefault = async (
  id: number,
  field: 'is_default_origin' | 'is_default_recipient',
  value: boolean = true
) => {
  const response = await request.post<CustomerAddress>(
    `/api/v1/logistics/customer-addresses/${id}/set-default/`,
    { field, value }
  );
  return response;
};

// 地址库查重：按姓名+电话+地址第一行+城市+州+邮编+国家这组身份字段查找是否已经存在等价记录
// （后端会做归一化比较，大小写/空格/电话格式差异不影响判断），查重范围是当前登录账号能看到的
// 地址库范围。查到返回对应记录，查不到 data 为 null。
export interface CustomerAddressDuplicateCheckParams {
  name: string;
  phone: string;
  address_line: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  // 员工/管理员代客户查重时必须传，收窄查重范围到这一个客户；客户账号自己查重不需要传
  customer?: number;
}

export const findDuplicateCustomerAddress = async (params: CustomerAddressDuplicateCheckParams) => {
  const response = await request.post<CustomerAddress | null>(
    '/api/v1/logistics/customer-addresses/find-duplicate/',
    params
  );
  return response;
};

// 下载/预览证件照片（blob，需要走鉴权请求，不能直接用 <a href>）
export const downloadCustomerAddressIdPhoto = async (id: number, side: 'front' | 'back') => {
  const response = await request.get(`/api/v1/logistics/customer-addresses/${id}/id-photo/${side}/`, {
    responseType: 'blob' as any,
  });
  return response;
};

// 预览发货包材标签（PDF blob）
export const previewShippingPackagingLabel = async (packagingId: number) => {
  const response = await request.post(
    '/api/v1/logistics/shipping-packagings/labels/preview/',
    { packaging_id: packagingId },
    { responseType: 'blob' as any }
  );
  return response;
};

// 下载发货包材标签（PDF blob）
export const downloadShippingPackagingLabel = async (packagingId: number) => {
  const response = await request.post(
    '/api/v1/logistics/shipping-packagings/labels/download/',
    { packaging_id: packagingId },
    { responseType: 'blob' as any }
  );
  return response;
};